import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../common/Button';

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  background: ${({ theme }) => theme.colors.gradient.primary};
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/assets/images/grid-pattern.svg');
    background-size: cover;
    opacity: 0.05;
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.md};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xl};
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroTextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    align-items: center;
    order: 2;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.white};
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.1em;
      left: 0;
      width: 100%;
      height: 0.1em;
      background-color: ${({ theme }) => theme.colors.accent};
      opacity: 0.3;
      z-index: -1;
    }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.light};
  margin-bottom: ${({ theme }) => theme.space.xl};
  max-width: 550px;
  line-height: 1.7;
  font-family: ${({ theme }) => theme.fonts.body};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const HeroImageWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px dashed ${({ theme }) => theme.colors.accent};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    top: 20px;
    left: 20px;
    z-index: 1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 1;
    margin-top: ${({ theme }) => theme.space.xl};
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 450px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(22, 211, 169, 0.3) 0%, rgba(77, 109, 227, 0.3) 100%);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, transparent 50%, rgba(0, 0, 0, 0.5) 100%);
    z-index: 2;
  }
`;

const BackgroundShape = styled(motion.div)<{ top?: string; left?: string; right?: string; bottom?: string; size: string; color: string; rotate?: string }>`
  position: absolute;
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border-radius: 50%;
  background-color: ${({ color }) => color};
  opacity: 0.15;
  top: ${({ top }) => top || 'auto'};
  left: ${({ left }) => left || 'auto'};
  right: ${({ right }) => right || 'auto'};
  bottom: ${({ bottom }) => bottom || 'auto'};
  transform: ${({ rotate }) => rotate ? `rotate(${rotate})` : 'none'};
  z-index: 1;
  filter: blur(50px);
`;

const HeroSection: React.FC = () => {
  return (
    <HeroContainer>
      <BackgroundShape 
        top="10%" 
        left="5%" 
        size="300px" 
        color="#16D3A9" 
        animate={{ 
          x: [0, 30, 0], 
          y: [0, 15, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ 
          repeat: Infinity, 
          duration: 20, 
          ease: "easeInOut" 
        }}
      />
      <BackgroundShape 
        top="60%" 
        right="10%" 
        size="200px" 
        color="#4D6DE3" 
        animate={{ 
          x: [0, -20, 0], 
          y: [0, 20, 0],
          scale: [1, 1.05, 1]
        }}
        transition={{ 
          repeat: Infinity, 
          duration: 15, 
          ease: "easeInOut" 
        }}
      />
      <BackgroundShape 
        bottom="10%" 
        left="30%" 
        size="250px" 
        color="#8C43FF" 
        animate={{ 
          x: [0, 15, 0], 
          y: [0, -25, 0],
          scale: [1, 1.15, 1]
        }}
        transition={{ 
          repeat: Infinity, 
          duration: 18, 
          ease: "easeInOut" 
        }}
      />
      
      <HeroContent>
        <HeroTextContent>
          <HeroTitle
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.1 }}
          >
            Data-driven digital <span>transformation</span> for modern businesses
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.3 }}
          >
            Transform your business with our cutting-edge digital strategies and 
            AI-powered analytics to unlock growth opportunities and stay ahead of the competition.
          </HeroSubtitle>
          
          <ButtonGroup
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.5 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              <Button 
                variant="accent" 
                size="large"
                style={{
                  background: 'linear-gradient(135deg, #16D3A9 0%, #4D6DE3 100%)',
                  boxShadow: '0 10px 20px rgba(22, 211, 169, 0.2)',
                  border: 'none'
                }}
              >
                Get Started
              </Button>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              <Button 
                variant="outline" 
                size="large"
                style={{
                  borderColor: '#16D3A9',
                  color: '#fff',
                  boxShadow: '0 5px 15px rgba(22, 211, 169, 0.1)'
                }}
              >
                Learn More
              </Button>
            </motion.div>
          </ButtonGroup>
        </HeroTextContent>
        
        <HeroImageWrapper
          initial={{ opacity: 0, scale: 0.9, x: 50 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.7 }}
        >
          <HeroImage />
        </HeroImageWrapper>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
